/* import __COLOCATED_TEMPLATE__ from './preview-settings-modal.hbs'; */
/* RESPONSIBLE TEAM: team-actions */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { localCopy } from 'tracked-toolbox';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type { PreviewSpoofingSettings } from 'embercom/components/workflows/preview/preview-module';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';
import type Store from '@ember-data/store';
import type FinSetupLocalizationService from 'embercom/services/fin-setup-localization-service';

// @ts-ignore
import PredicateGroup from 'predicates/models/predicate-group';
// @ts-ignore
import { copy } from 'ember-copy';
import { type TrackedArray } from 'tracked-built-ins';

interface PreviewSettingsModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: PreviewSpoofingSettings, locale?: string, filtersCount?: number) => void;
  currentBrandId: string | null;
  currentUser?: UserSummary | null;
  shouldCallRealActions: boolean;
  previewLocale?: string;
  disableAutoLanguageDetection?: boolean;
  previewChannel?: 'email' | 'chat';
  asNewLead?: boolean;
  inSearchMode?: boolean;
  inAudienceSearchMode?: boolean;
  currentContentSegmentIds?: TrackedArray<string>;
}

interface Signature {
  Args: PreviewSettingsModalArgs;
  Element: HTMLDivElement;
}

export default class PreviewSettingsModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare finSetupLocalizationService: FinSetupLocalizationService;

  @tracked ruleset: $TSFixMe;
  @tracked _internalSearchMode: boolean | null = null;
  @tracked _lastSavedContentSegmentIds: string[] = [];
  @localCopy('args.currentContentSegmentIds', [])
  declare selectedContentSegmentIds: TrackedArray<string>;

  @localCopy('args.currentBrandId', null) declare brandId: string | null;
  @localCopy('args.currentUser', null) declare selectedUser: UserSummary | null;
  @localCopy('args.shouldCallRealActions', false)
  declare shouldReadAndUpdateActualUserData: boolean;
  @localCopy('args.asNewLead', false) declare asNewLead: boolean;
  @localCopy('args.previewLocale') declare selectedLocale: string | undefined;
  @service declare store: Store;

  constructor(owner: unknown, args: PreviewSettingsModalArgs) {
    super(owner, args);
    this.ruleset = this.setUpRuleset();
  }

  private addOrRemoveClickedSegment(segmentId: string) {
    if (this.selectedContentSegmentIds.includes(segmentId)) {
      this.selectedContentSegmentIds.removeObject(segmentId);
    } else {
      if (this.args.previewChannel === 'email') {
        this.selectedContentSegmentIds.clear();
      }
      this.selectedContentSegmentIds.pushObject(segmentId);
    }
  }

  @action
  onSelectAudience(audience: { id: string }) {
    this.addOrRemoveClickedSegment(audience.id);

    if (this.selectedContentSegmentIds.length > 0) {
      this.selectedUser = null;
      this.brandId = null;
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'audience',
      place: 'playground_test_settings_modal',
    });
  }

  willDestroy() {
    super.willDestroy();
    this.finSetupLocalizationService.stopPolling();
  }

  get inSearchMode() {
    let mode = this._internalSearchMode ?? this.args.inSearchMode ?? false;

    // Force localCopy sync when entering search mode to ensure fresh state
    if (mode) {
      // Access localCopy properties to trigger synchronization with args
      this.selectedUser;
      this.asNewLead;
    }

    return mode;
  }

  get brandIsDisabled() {
    // brand is only modified when using impersonation
    return !this.selectedUser && !this.asNewLead;
  }

  get localeIsDisabled() {
    return this.args.previewChannel === 'email' || !!this.selectedUser || this.asNewLead;
  }

  get autoLanguageDetectionIsDisabled() {
    return this.args.disableAutoLanguageDetection && !this.localeIsDisabled;
  }

  @action
  onSave() {
    this.args.onSave(
      {
        user: this.selectedUser ?? null,
        brandId: this.brandId ?? null,
        shouldCallRealActions: this.shouldReadAndUpdateActualUserData,
        asNewLead: this.asNewLead,
        selectedContentSegmentIds: this.selectedContentSegmentIds,
      },
      this.selectedLocale,
      this.ruleset.predicateGroup.predicates.length,
    );

    this._lastSavedContentSegmentIds = [...this.selectedContentSegmentIds];
    let nullImpersonationType = this.asNewLead ? 'new_lead' : 'preview_user';

    this.intercomEventService.trackAnalyticsEvent({
      action: 'saved',
      object: 'impersonation_settings',
      place: 'preview_settings_modal',
      // impersonation setting
      impersonation: this.selectedUser ? 'real_user' : nullImpersonationType,
      user_role: this.selectedUser?.role,
      brand: this.brandId ? 'chose_brand' : 'default_brand',
      language: this.selectedLocale !== 'auto' ? 'chose_language' : 'auto_detect',
      action_type: this.shouldReadAndUpdateActualUserData ? 'live_data' : 'sample_data',
      as_new_lead: this.asNewLead,
      filters_count: this.ruleset.predicateGroup.predicates.length,
    });

    this.onClose();
  }

  @action
  onCancel() {
    this.selectedUser = this.args.currentUser ?? null;
    this.shouldReadAndUpdateActualUserData = this.args.shouldCallRealActions;
    this.brandId = this.args.currentBrandId;
    this.asNewLead = this.args.asNewLead ?? false;
    this.selectedLocale = this.args.previewLocale;
    this.selectedContentSegmentIds.clear();
    this.selectedContentSegmentIds.pushObjects(this._lastSavedContentSegmentIds);

    this.onClose();
  }

  @action
  onClose() {
    this._internalSearchMode = null;
    this.args.onClose();
  }

  @action
  handleBrandSelection(brandId: string | null) {
    this.brandId = brandId;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'brand',
      place: 'preview_settings_modal',
    });
  }

  @action
  handleUserSelection(user: UserSummary | null) {
    if (user?.id === 'newly-created-lead') {
      this.asNewLead = true;
      this.selectedUser = null;
    } else {
      this.asNewLead = false;
      this.selectedUser = user;
    }

    this.selectedContentSegmentIds.clear();

    if (!user) {
      this.brandId = null;
      if (this.args.disableAutoLanguageDetection) {
        this.selectedLocale = this.appService.app.locale || 'en';
      } else {
        this.selectedLocale = 'auto';
      }
    } else {
      this.selectedLocale = 'auto';
    }

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'user',
      place: 'preview_settings_modal',
    });
  }

  @action
  onUpdatePredicates(predicates: $TSFixMe) {
    this.ruleset.set('predicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'filters',
      place: 'preview_settings_modal',
    });
  }

  @action
  onUpdateRolePredicates(predicates: $TSFixMe) {
    this.ruleset.set('rolePredicateGroup.predicates', copy(predicates));
    this.ruleset.fetchAudiencePreviewTask.perform();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'role_filter',
      place: 'preview_settings_modal',
    });
  }

  @action
  toggleSearchMode() {
    this._internalSearchMode = !this.inSearchMode;

    if (this._internalSearchMode) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'impersonated_user_search',
        place: 'preview_settings_modal',
      });
    }
  }

  get dataConnectorLink() {
    let translatedLink = this.intl.t(
      'operator.custom-bot.editor.preview.preview-settings.data-connector-is-triggered.data-connector-link',
    );
    let url = this.router.urlFor('apps.app.settings.app-settings.custom-actions');
    return sanitizeHtml(
      `<a class="cursor-pointer text-neutral-default font-semibold" href="${url}" target="_blank">${translatedLink}</a>`,
    );
  }

  get canUseFinPreviewImpersonation() {
    return this.appService.app.canUseFinPreviewImpersonation;
  }

  get canUseFinPreviewAudience() {
    return this.appService.app.canUseFeature('fin-preview-audience');
  }

  get shouldShowActionDataRadioButtons() {
    return this.canUseFinPreviewImpersonation && !!this.selectedUser;
  }

  get previewSettings(): PreviewSpoofingSettings {
    return {
      user: this.selectedUser ?? null,
      brandId: this.brandId ?? null,
      shouldCallRealActions: this.shouldReadAndUpdateActualUserData,
      asNewLead: this.asNewLead,
      selectedContentSegmentIds: this.selectedContentSegmentIds,
    };
  }

  @action
  handleLanguageChange(locale: string) {
    this.selectedLocale = locale;

    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'language',
      place: 'preview_settings_modal',
    });
  }

  setUpRuleset() {
    let newRuleset = this.store.createRecord('matching-system/ruleset');
    newRuleset.set('predicateGroup', this.store.createFragment('predicates/predicate-group'));
    newRuleset.set('clientPredicateGroup', this.store.createFragment('predicates/predicate-group'));
    // must properly define the default user roles to be all of them
    newRuleset.set(
      'rolePredicateGroup',
      this.store.createFragment('predicates/predicate-group', {
        predicates: PredicateGroup.convertRawPredicates(this.store, [
          {
            attribute: 'role',
            comparison: 'in',
            type: 'role',
            value: ['user_role', 'contact_role'],
          },
        ]),
      }),
    );
    newRuleset.set(
      'defaultPredicateGroup',
      this.store.createFragment('predicates/predicate-group'),
    );

    return newRuleset;
  }

  @action
  trackShouldReadAndUpdateActualUserDataChange(actionType: 'live_data' | 'sample_data') {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'changed',
      object: 'should_call_real_actions_radio_button',
      place: 'preview_settings_modal',
      action_type: actionType,
    });
  }

  @action
  resetToDefaultSettings() {
    this.brandId = null;
    this.selectedUser = null;
    this.shouldReadAndUpdateActualUserData = false;
    if (this.args.previewLocale) {
      if (this.args.disableAutoLanguageDetection) {
        this.selectedLocale = this.appService.app.locale || 'en';
      } else {
        this.selectedLocale = 'auto';
      }
    }
    this.asNewLead = false;
    this.selectedContentSegmentIds.clear();

    this.intercomEventService.trackAnalyticsEvent({
      action: 'reset',
      object: 'impersonation_settings',
      place: 'preview_settings_modal',
    });
  }

  @action
  startPollingTranslationStatus() {
    this.finSetupLocalizationService.startPolling();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::Modals::PreviewSettingsModal': typeof PreviewSettingsModal;
    'workflows/preview/modals/preview-settings-modal': typeof PreviewSettingsModal;
  }
}
